import React, { useState, useRef, useEffect } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { formatDateTime, formatTimeForInput, formatDateTimeLocal, parseDateTimeLocal } from '../../utils/dateUtils';

interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  id?: string;
  name?: string;
  error?: string;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  placeholder = "dd/mm/yyyy hh:mm",
  className = "",
  required = false,
  id,
  name,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const now = new Date();
  /** The value as a date, or null when it is missing or unreadable. */
  const asDate = (v?: string): Date | null => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const [selectedDate, setSelectedDate] = useState<Date>(asDate(value) || now);
  const [currentMonth, setCurrentMonth] = useState<Date>(now); // Always default to current month
  const [timeInput, setTimeInput] = useState(formatTimeForInput(asDate(value) || now) || '09:00');
  const [manualInput, setManualInput] = useState(
    asDate(value) ? formatDateTime(asDate(value)!) : 'dd/mm/yyyy, hh:mm'
  );
  const [rawInput, setRawInput] = useState(
    asDate(value) ? formatDateTime(asDate(value)!).replace(/\D/g, '') : ''
  );
  const inputRef = useRef<HTMLInputElement>(null);

  // Update internal state when value prop changes
  useEffect(() => {
    const date = asDate(value);
    if (date) {
      setSelectedDate(date);
      setCurrentMonth(date);
      setTimeInput(formatTimeForInput(date));
      const formatted = formatDateTime(date);
      setManualInput(formatted);
      setRawInput(formatted.replace(/\D/g, ''));
    } else {
      setManualInput('dd/mm/yyyy, hh:mm am/pm'); // Show template when empty
      setRawInput('');
    }
  }, [value]);

  const containerRef = useRef<HTMLDivElement>(null);



  // formatDateTimeLocal is imported from dateUtils. A local wrapper of the same
  // name used to sit here calling itself, which shadowed the import and blew
  // the stack the moment anyone picked a date or a time.

  // Parse manual input with better validation
  function parseManualInput(input: string): Date | null {
    try {
      // Try the main DD/MM/YYYY, HH:MM AM/PM format
      const ddmmyyyyMatch = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s*(\d{1,2}):(\d{2})\s*(am|pm)$/i);
      if (ddmmyyyyMatch) {
        const [, day, month, year, hours, minutes, ampm] = ddmmyyyyMatch;
        
        const dayNum = parseInt(day);
        const monthNum = parseInt(month);
        const yearNum = parseInt(year);
        let hoursNum = parseInt(hours);
        const minutesNum = parseInt(minutes);
        
        // Convert 12-hour to 24-hour format
        if (ampm.toLowerCase() === 'pm' && hoursNum !== 12) {
          hoursNum += 12;
        } else if (ampm.toLowerCase() === 'am' && hoursNum === 12) {
          hoursNum = 0;
        }
        
        // Validate ranges
        if (dayNum < 1 || dayNum > 31) return null;
        if (monthNum < 1 || monthNum > 12) return null;
        if (yearNum < 1900 || yearNum > 2100) return null;
        if (hoursNum < 0 || hoursNum > 23) return null;
        if (minutesNum < 0 || minutesNum > 59) return null;
        
        const date = new Date(yearNum, monthNum - 1, dayNum, hoursNum, minutesNum);
        
        // Check if date is valid (handles things like Feb 30th)
        if (date.getFullYear() === yearNum && 
            date.getMonth() === monthNum - 1 && 
            date.getDate() === dayNum) {
          return date;
        }
      }
      
      // Try the old DD/MM/YYYY, HH:MM format (24-hour) for backward compatibility
      const ddmmyyyy24Match = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s*(\d{1,2}):(\d{2})$/);
      if (ddmmyyyy24Match) {
        const [, day, month, year, hours, minutes] = ddmmyyyy24Match;
        
        const dayNum = parseInt(day);
        const monthNum = parseInt(month);
        const yearNum = parseInt(year);
        const hoursNum = parseInt(hours);
        const minutesNum = parseInt(minutes);
        
        // Validate ranges
        if (dayNum < 1 || dayNum > 31) return null;
        if (monthNum < 1 || monthNum > 12) return null;
        if (yearNum < 1900 || yearNum > 2100) return null;
        if (hoursNum < 0 || hoursNum > 23) return null;
        if (minutesNum < 0 || minutesNum > 59) return null;
        
        const date = new Date(yearNum, monthNum - 1, dayNum, hoursNum, minutesNum);
        
        // Check if date is valid (handles things like Feb 30th)
        if (date.getFullYear() === yearNum && 
            date.getMonth() === monthNum - 1 && 
            date.getDate() === dayNum) {
          return date;
        }
      }
      
      // Try other formats as fallback
      const formats = [
        /^(\d{1,2})-(\d{1,2})-(\d{4})\s*(\d{1,2}):(\d{2})$/,
        /^(\d{4})-(\d{1,2})-(\d{1,2})\s*(\d{1,2}):(\d{2})$/,
      ];

      for (const format of formats) {
        const match = input.match(format);
        if (match) {
          let day, month, year, hours, minutes;
          
          if (format === formats[1]) { // YYYY-MM-DD format
            [, year, month, day, hours, minutes] = match;
          } else { // DD-MM-YYYY format
            [, day, month, year, hours, minutes] = match;
          }
          
          const dayNum = parseInt(day);
          const monthNum = parseInt(month);
          const yearNum = parseInt(year);
          const hoursNum = parseInt(hours);
          const minutesNum = parseInt(minutes);
          
          // Same validation as above
          if (dayNum < 1 || dayNum > 31) continue;
          if (monthNum < 1 || monthNum > 12) continue;
          if (yearNum < 1900 || yearNum > 2100) continue;
          if (hoursNum < 0 || hoursNum > 23) continue;
          if (minutesNum < 0 || minutesNum > 59) continue;
          
          const date = new Date(yearNum, monthNum - 1, dayNum, hoursNum, minutesNum);
          
          if (date.getFullYear() === yearNum && 
              date.getMonth() === monthNum - 1 && 
              date.getDate() === dayNum) {
            return date;
          }
        }
      }
      
      return null;
    } catch {
      return null;
    }
  }



  // Handle manual input change with cursor management
  const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const inputValue = e.target.value;
    
    // Extract only numbers from the new input
    const numbersOnly = inputValue.replace(/\D/g, '');
    
    // Apply mask formatting
    const formatted = applyInputMask(numbersOnly);
    setManualInput(formatted);
    setRawInput(numbersOnly);
    
    // Simple cursor positioning - just put cursor after the last number entered
    let newCursorPos = 0;
    
    if (numbersOnly.length === 0) {
      newCursorPos = 0;
    } else {
      // Find the position right after the last entered digit in the formatted string
      let numbersFound = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
          numbersFound++;
          if (numbersFound === numbersOnly.length) {
            newCursorPos = i + 1;
            break;
          }
        }
      }
    }
    
    // Update cursor position after state update
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
    
    // Try to parse and update date
    const parsedDate = parseManualInput(formatted);
    if (parsedDate) {
      setSelectedDate(parsedDate);
      setCurrentMonth(parsedDate);
      setTimeInput(formatTimeForInput(parsedDate));
      onChange(formatDateTimeLocal(parsedDate));
    } else if (numbersOnly === '') {
      onChange('');
    }
  };

  // Apply input mask formatting - always maintain template structure
  const applyInputMask = (numbers: string): string => {
    const template = 'dd/mm/yyyy, hh:mm am/pm';
    let result = '';
    let numberIndex = 0;
    
    for (let i = 0; i < template.length; i++) {
      const templateChar = template[i];
      
      if (templateChar === 'd' || templateChar === 'm' || templateChar === 'y' || templateChar === 'h') {
        if (numberIndex < numbers.length) {
          result += numbers[numberIndex];
          numberIndex++;
        } else {
          result += '_';
        }
      } else {
        result += templateChar; // Keep separators like /, :, comma, space, and am/pm
      }
    }
    
    return result;
  };



  // Handle date selection from calendar
  const handleDateSelect = (date: Date) => {
    const [hours, minutes] = timeInput.split(':').map(Number);
    const newDate = new Date(date);
    newDate.setHours(hours, minutes);
    
    setSelectedDate(newDate);
    const formatted = formatDateTime(newDate);
    setManualInput(formatted);
    setRawInput(formatted.replace(/\D/g, ''));
    onChange(formatDateTimeLocal(newDate));
  };

  // Handle time change
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setTimeInput(newTime);
    
    const [hours, minutes] = newTime.split(':').map(Number);
    const newDate = new Date(selectedDate);
    newDate.setHours(hours, minutes);
    
    setSelectedDate(newDate);
    const formatted = formatDateTime(newDate);
    setManualInput(formatted);
    setRawInput(formatted.replace(/\D/g, ''));
    onChange(formatDateTimeLocal(newDate));
  };

  // Calendar navigation
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentMonth(newMonth);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const today = new Date();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = date.toDateString() === selectedDate.toDateString();
      
      days.push({
        date,
        day: date.getDate(),
        isCurrentMonth,
        isToday,
        isSelected
      });
    }
    
    return days;
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const days = generateCalendarDays();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

    return (
    <div className="relative" ref={containerRef}>
      <div className="flex gap-2">
        <div className="flex-1">
                                             <Input
                   ref={inputRef}
                   type="text"
                   value={manualInput}
                   onChange={handleManualInputChange}
                   className={`${className} font-mono text-sm`}
                   required={required}
                   id={id}
                   name={name}
                   style={{ 
                     fontFamily: 'monospace',
                     color: manualInput.includes('_') ? '#6b7280' : 'inherit',
                     letterSpacing: '0.05em',
                     fontSize: '14px',
                     height: '36px'
                   }}
                   onSelect={(e: React.SyntheticEvent<HTMLInputElement>) => {
                     // Allow natural text selection
                     e.stopPropagation();
                   }}
                 />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          className="px-2 h-9"
        >
          <Calendar className="h-3 w-3" />
        </Button>
      </div>
      
      {error && (
        <div className="mt-1 text-sm text-red-600">{error}</div>
      )}

      {isOpen && (
                       <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 p-3 w-72">
                       {/* Time picker */}
             <div className="border-b pb-3 mb-3">
               <div className="flex items-center justify-center gap-2">
                 <Clock className="h-3 w-3 text-gray-500" />
                 <label className="text-xs font-medium">Time:</label>
                 <Input
                   type="time"
                   value={timeInput}
                   onChange={handleTimeChange}
                   className="w-20 h-7 text-xs"
                 />
               </div>
             </div>

                     {/* Month navigation */}
           <div className="flex items-center justify-between mb-3">
             <Button
               type="button"
               variant="ghost"
               size="sm"
               onClick={() => navigateMonth('prev')}
               className="h-6 w-6 p-0"
             >
               <ChevronLeft className="h-3 w-3" />
             </Button>
             <h3 className="text-sm font-semibold">
               {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
             </h3>
             <Button
               type="button"
               variant="ghost"
               size="sm"
               onClick={() => navigateMonth('next')}
               className="h-6 w-6 p-0"
             >
               <ChevronRight className="h-3 w-3" />
             </Button>
           </div>

                     {/* Calendar grid */}
           <div className="grid grid-cols-7 gap-1 mb-3">
             {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
               <div key={day} className="text-center text-xs font-medium text-gray-500 p-1">
                 {day}
               </div>
             ))}
             {days.map((day, index) => (
               <button
                 key={index}
                 type="button"
                 onClick={() => handleDateSelect(day.date)}
                 className={`
                   p-1 text-xs rounded hover:bg-blue-100 transition-colors w-8 h-8 flex items-center justify-center
                   ${!day.isCurrentMonth ? 'text-gray-300' : 'text-gray-900'}
                   ${day.isToday ? 'bg-blue-100 font-semibold' : ''}
                   ${day.isSelected ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                 `}
               >
                 {day.day}
               </button>
             ))}
           </div>

                     {/* Action buttons */}
           <div className="flex justify-between gap-2 mt-3">
             <Button
               type="button"
               size="sm"
                    onClick={() => {
                        const now = new Date();
                        setSelectedDate(now);
                        setCurrentMonth(now);
                        setTimeInput(formatTimeForInput(now));
                        const formatted = formatDateTime(now);
                        setManualInput(formatted);
                        setRawInput(formatted.replace(/\D/g, ''));
                        onChange(formatDateTimeLocal(now));
                      }}
               className="bg-gray-600 hover:bg-gray-700 h-7 px-2 text-xs"
             >
               Now
             </Button>
             
             <div className="flex gap-1">
               <Button
                 type="button"
                 variant="outline"
                 size="sm"
                 onClick={() => setIsOpen(false)}
                 className="h-7 px-2 text-xs"
               >
                 Cancel
               </Button>
               <Button
                 type="button"
                 size="sm"
                 onClick={() => {
                                          // Apply the current selection. The time is read
                        // defensively: an empty or half-typed field used to make
                        // setHours produce an invalid date, and Apply silently
                        // did nothing.
                        const [h, m] = timeInput.split(':').map(Number);
                        const newDate = new Date(selectedDate);
                        newDate.setHours(
                          Number.isFinite(h) ? h : selectedDate.getHours(),
                          Number.isFinite(m) ? m : selectedDate.getMinutes()
                        );
                        
                        const formatted = formatDateTime(newDate);
                        setManualInput(formatted);
                        setRawInput(formatted.replace(/\D/g, ''));
                        onChange(formatDateTimeLocal(newDate));
                        setIsOpen(false);
                 }}
                 className="bg-blue-600 hover:bg-blue-700 h-7 px-2 text-xs"
               >
                 Apply
               </Button>
             </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker; 